import * as yup from "yup";

// ---------------------------------------------------------------------------
// Live-In Aide sub-flow — form state, yup schema, and step order.
//
// Mirrors the STRUCTURE of coApplicantState.ts (step-key array + resume
// helper), but the aide has its own screen set: no income / asset / student /
// demographics steps. The aide is an OCCUPANT, not an income applicant. The
// `LiveInAideState` type is the single shared shape consumed by the aide
// screens (Tasks 3-10), the submit endpoint (Task 12), and the DocuSeal
// prefill builder (Task 13).
// ---------------------------------------------------------------------------

/** Ordered step keys for the aide sub-flow (9 screens). */
export type AideStepKey =
  | "identity"
  | "rentalHistory"
  | "household"
  | "careAttestations"
  | "medicalExpense"
  | "criminalScreening"
  | "statesLived"
  | "leasingAck"
  | "review";

export const AIDE_STEPS: AideStepKey[] = [
  "identity",
  "rentalHistory",
  "household",
  "careAttestations",
  "medicalExpense",
  "criminalScreening",
  "statesLived",
  "leasingAck",
  "review",
];

// Government-issued ID types — mirrors the co-applicant / head-of-household set.
export const AIDE_GOV_ID_TYPE_KEYS = [
  "driversLicense",
  "stateId",
  "passport",
  "militaryId",
  "itin",
  "foreignGovId",
  "other",
] as const;
export type AideGovIdType = (typeof AIDE_GOV_ID_TYPE_KEYS)[number] | "";

export const MEDICAL_FEE_FREQUENCIES = ["yearly", "monthly", "weekly"] as const;
export type MedicalFeeFrequency = (typeof MEDICAL_FEE_FREQUENCIES)[number] | "";

// ---------------------------------------------------------------------------
// Per-screen field shapes
// ---------------------------------------------------------------------------

/** Screen 1 — Confirm identity (Questionnaire p1). */
export interface AideIdentityData {
  dob: string;
  ssn: string;
  noSsn: boolean;
  govIdType: AideGovIdType;
  govId: string;
  homePhone: string;
  mobilePhone: string;
  residentServedName: string;
  exemptNonCitizen: boolean;
  exemptSenior: boolean;
}

/** A single residence block (current or previous). */
export interface AideResidence {
  landlord: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  startDate: string;
  endDate: string;
}

/** Screen 2 — Rental history (Questionnaire p1). */
export interface AideRentalHistoryData {
  current: AideResidence;
  previous: AideResidence;
}

/** Screen 3 — Housing & household (Questionnaire p1-2). Y/N. */
export interface AideHouseholdData {
  hudAssistance: boolean | null;
  otherFamilyMoving: boolean | null;
  pets: boolean | null;
  goodStanding: boolean | null;
  evicted: boolean | null;
  evictedExplain: string;
}

/** Screen 4 — HUD Live-In Aide attestations (Questionnaire p2). Y/N. */
export interface AideCareAttestationsData {
  essentialCare: boolean | null;
  obligatedSupport: boolean | null;
  wouldNotLiveExcept: boolean | null;
}

/** Screen 5 — Medical expense (Questionnaire p2). */
export interface AideMedicalExpenseData {
  residentPaysFee: boolean | null;
  amount: string;
  frequency: MedicalFeeFrequency;
}

/** Screen 6 — Criminal screening (Questionnaire p3). Y/N + conditional explain. */
export interface AideCriminalData {
  legalActions10y: boolean | null;
  legalActionsExplain: string;
  sexOffenderLifetime: boolean | null;
  sexOffenderOther: boolean | null;
  marijuana: boolean | null;
  alcoholAbuse: boolean | null;
}

/** Screen 8 — Leasing acknowledgements (Questionnaire p3-4). Y/N. */
export interface AideLeasingAckData {
  readLease: boolean | null;
  agreeAbide: boolean | null;
  understandEviction: boolean | null;
  noOccupancyRights: boolean | null;
  vacateIfAbsent: boolean | null;
  vacateOnDeath: boolean | null;
}

/**
 * The full aide sub-flow state. Screen 7 (states lived) is stored as a flat
 * array of 2-letter codes ("DC" for Washington D.C.) on `statesLived`.
 */
export interface LiveInAideState {
  identity: AideIdentityData;
  rentalHistory: AideRentalHistoryData;
  household: AideHouseholdData;
  careAttestations: AideCareAttestationsData;
  medicalExpense: AideMedicalExpenseData;
  criminal: AideCriminalData;
  statesLived: string[];
  leasingAck: AideLeasingAckData;
}

// ---------------------------------------------------------------------------
// Empty / initial state
// ---------------------------------------------------------------------------

const emptyResidence = (): AideResidence => ({
  landlord: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  startDate: "",
  endDate: "",
});

export const emptyLiveInAideState: LiveInAideState = {
  identity: {
    dob: "",
    ssn: "",
    noSsn: false,
    govIdType: "",
    govId: "",
    homePhone: "",
    mobilePhone: "",
    residentServedName: "",
    exemptNonCitizen: false,
    exemptSenior: false,
  },
  rentalHistory: {
    current: emptyResidence(),
    previous: emptyResidence(),
  },
  household: {
    hudAssistance: null,
    otherFamilyMoving: null,
    pets: null,
    goodStanding: null,
    evicted: null,
    evictedExplain: "",
  },
  careAttestations: {
    essentialCare: null,
    obligatedSupport: null,
    wouldNotLiveExcept: null,
  },
  medicalExpense: {
    residentPaysFee: null,
    amount: "",
    frequency: "",
  },
  criminal: {
    legalActions10y: null,
    legalActionsExplain: "",
    sexOffenderLifetime: null,
    sexOffenderOther: null,
    marijuana: null,
    alcoholAbuse: null,
  },
  statesLived: [],
  leasingAck: {
    readLease: null,
    agreeAbide: null,
    understandEviction: null,
    noOccupancyRights: null,
    vacateIfAbsent: null,
    vacateOnDeath: null,
  },
};

// ---------------------------------------------------------------------------
// Validation schema (yup 0.32)
//
// Required rules cover the identity essentials + the two documented conditional
// rules (criminal explain iff legalActions10y === true; medical amount +
// frequency iff residentPaysFee === true). Y/N grids on the household / care /
// leasing screens validate per-screen in their own components; the top-level
// schema keeps them optional so a partially-filled state can still be persisted.
// ---------------------------------------------------------------------------

export const liveInAideSchema = yup.object({
  household: yup.object({
    evicted: yup.boolean().nullable().required(),
    evictedExplain: yup.string().when("evicted", {
      is: true,
      then: yup.string().required(),
      otherwise: yup.string().notRequired(),
    }),
  }),
  identity: yup.object({
    dob: yup.string().required(),
    ssn: yup.string().when("noSsn", {
      is: true,
      then: yup.string().notRequired(),
      otherwise: yup.string().required(),
    }),
    noSsn: yup.boolean(),
    govId: yup.string().required(),
    residentServedName: yup.string().required(),
  }),
  criminal: yup.object({
    legalActions10y: yup.boolean().nullable(),
    legalActionsExplain: yup.string().when("legalActions10y", {
      is: true,
      then: yup.string().required(),
      otherwise: yup.string().notRequired(),
    }),
  }),
  medicalExpense: yup.object({
    residentPaysFee: yup.boolean().nullable(),
    amount: yup.string().when("residentPaysFee", {
      is: true,
      then: yup.string().required(),
      otherwise: yup.string().notRequired(),
    }),
    frequency: yup.string().when("residentPaysFee", {
      is: true,
      then: yup.string().required(),
      otherwise: yup.string().notRequired(),
    }),
  }),
});

/**
 * Resume helper — mirrors computeCoApplicantResume. `saved` maps step keys to a
 * truthy "this step has been persisted" flag (the aide app stores each screen's
 * slice on prop_application.applicationData under its AideStepKey).
 */
export const computeAideResume = (
  saved: Partial<Record<AideStepKey, unknown>>
): { completedSteps: number[]; resumeStep: number } => {
  const completedSteps = AIDE_STEPS.flatMap((key, idx) =>
    saved[key] ? [idx] : []
  );
  const firstIncomplete = AIDE_STEPS.findIndex((key) => !saved[key]);
  const resumeStep =
    firstIncomplete === -1 ? AIDE_STEPS.length - 1 : firstIncomplete;
  return { completedSteps, resumeStep };
};
